import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Box,
  List,
  Card,
  CardMedia,
  CardContent,
  Chip,
  Stack,
  CircularProgress,
  Button,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  ArrowBack,
  Refresh,
} from '@mui/icons-material';
import { useRecipe } from '../context/RecipeContext';
import { useAuth } from '../hooks/useAuth';
import { mockGenerateRecipes, mockGetPopularRecipes } from '../utils/MockAPI';

const Results: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { 
    results, 
    setResults, 
    selectedCategories, 
    mode, 
    setMode,
    getPopularRecipesCache,
    setPopularRecipesCache,
    clearPopularRecipesCache
  } = useRecipe();
  const { userId } = useAuth();
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'error' as 'error' | 'success' });
  const [regenerateDialog, setRegenerateDialog] = useState(false);

  // Detect if we're on the /popular route and set mode accordingly
  useEffect(() => {
    if (location.pathname === '/popular' && mode !== 'popular') {
      setMode('popular');
    }
  }, [location.pathname, mode, setMode]);

  const fetchResults = useCallback(async (requestedMode?: 'generate' | 'regenerate' | 'find' | 'popular') => {
    const currentMode = requestedMode || mode;
    setLoading(true);
    try {
      let newResults;
      
      if (currentMode === 'popular') {
        // Check cache first
        const cached = getPopularRecipesCache();
        if (cached) {
          newResults = cached.recipes;
          setSnackbar({ open: true, message: 'Popular recipes loaded from cache!', severity: 'success' });
        } else {
          // Fetch from API and cache the result (request 20 popular recipes)
          newResults = await mockGetPopularRecipes(20, 1);
          const cacheData = {
            recipes: newResults,
            timestamp: Date.now(),
            expiresAt: Date.now() + (60 * 60 * 1000) // 1 hour from now
          };
          setPopularRecipesCache(cacheData);
          setSnackbar({ open: true, message: 'Popular recipes loaded!', severity: 'success' });
        }
      } else {
        newResults = await mockGenerateRecipes({ selections: selectedCategories, mode: currentMode, userId });
        setSnackbar({ open: true, message: currentMode === 'regenerate' ? 'New recipes generated!' : 'Recipes generated successfully!', severity: 'success' });
      }
      
      setResults(newResults);
    } catch (error) {
      console.error('Error fetching results:', error);
      setSnackbar({ open: true, message: 'Failed to generate recipes. Please try again.', severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, [selectedCategories, mode, setResults, userId, getPopularRecipesCache, setPopularRecipesCache]);

  useEffect(() => {
    if (results.length === 0) {
      fetchResults();
    }
  }, [results.length, fetchResults]);

  const handleRegenerateClick = () => {
    setRegenerateDialog(true);
  };

  const handleSameIngredients = () => {
    setRegenerateDialog(false);
    setMode('regenerate');
    fetchResults('regenerate'); // Pass true for isRegenerate
  };

  const handleChangeIngredients = () => {
    setRegenerateDialog(false);
    setMode('generate');
    navigate('/composer');
  };

  const handleCardClick = (id: string) => {
    navigate(`/recipe/${id}`);
  };

  const selectedChips = Object.values(selectedCategories).flat();

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', maxWidth: '1200px', mx: 'auto' }}>
      <AppBar position="static">
        <Toolbar>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton edge="start" sx={{ color: 'white' }} onClick={() => mode === 'popular' ? navigate('/') : navigate('/composer')} aria-label={mode === 'popular' ? "Back to home" : "Back to composer"}>
              <ArrowBack />
            </IconButton>
            <Typography variant="body2" sx={{ ml: 1, fontSize: { xs: '1rem', md: '1.25rem' }, fontWeight: 'bold', color: 'white', textShadow: '1px 1px 2px rgba(0,0,0,0.3)', cursor: 'pointer', '&:hover': { opacity: 0.8 } }} onClick={() => mode === 'popular' ? navigate('/') : navigate('/composer')}>
              {mode === 'popular' ? 'Home' : 'Change Ingredients'}
            </Typography>
          </Box>
          <Typography variant="h6" sx={{ flexGrow: 1, textAlign: 'center', fontSize: { xs: '1rem', md: '1.25rem' }, fontWeight: 'bold', color: 'white', textShadow: '1px 1px 2px rgba(0,0,0,0.3)' }}>
            {mode === 'popular' ? 'Popular Recipes' : 'Recipe Results'}
          </Typography>
          {mode === 'popular' ? (
            <Button 
              sx={{ color: 'white', fontSize: { xs: '1rem', md: '1.25rem' }, fontWeight: 'bold', textShadow: '1px 1px 2px rgba(0,0,0,0.3)' }}
              startIcon={<Refresh />} 
              onClick={() => {
                clearPopularRecipesCache();
                fetchResults('popular');
              }} 
              disabled={loading} 
              aria-label="Refresh popular recipes"
            >
              Refresh
            </Button>
          ) : (
            <Button 
              sx={{ color: 'white', fontSize: { xs: '1rem', md: '1.25rem' }, fontWeight: 'bold', textShadow: '1px 1px 2px rgba(0,0,0,0.3)' }}
              startIcon={<Refresh />} 
              onClick={handleRegenerateClick} 
              disabled={loading} 
              aria-label="Create another recipe"
            >
              Create Another
            </Button>
          )}
        </Toolbar>
      </AppBar>

      {/* Filters and Hint Section */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: { xs: 'stretch', md: 'center' },
          gap: { xs: 1, md: 2 },
          p: { xs: 1, md: 2 },
          borderBottom: selectedChips.length > 0 ? 1 : 0,
          borderColor: 'divider',
        }}
      >
        {selectedChips.length > 0 && (
          <Box sx={{ flex: { xs: 'none', md: 1 } }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Filters:
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap">
              {selectedChips.map((chip, index) => (
                <Chip key={index} label={chip} size="small" />
              ))}
            </Stack>
          </Box>
        )}
        <Box sx={{ flex: { xs: 'none', md: selectedChips.length > 0 ? 'none' : 1 }, textAlign: { xs: 'left', md: 'right' } }}>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              fontSize: '0.875rem',
              fontStyle: 'italic',
              fontWeight: 'bold'
            }}
          >
            Tap any recipe card to view full details
          </Typography>
        </Box>
      </Box>

      <Box sx={{ flexGrow: 1, overflowY: 'auto', p: { xs: 1, md: 2 } }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
            <CircularProgress />
          </Box>
        ) : results.length === 0 ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: '50vh',
              textAlign: 'center',
              px: 3
            }}
          >
            <Typography variant="h5" component="h2" sx={{ mb: 2, fontWeight: 'bold' }}>
              {mode === 'popular' ? 'No Popular Recipes Found' : 'No Recipes Found'}
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 4, maxWidth: 400 }}>
              {mode === 'popular' 
                ? 'No popular recipes are currently available. Try creating your own recipe!' 
                : 'No recipes were found matching your search criteria. Try creating a new recipe!.'
              }
            </Typography>
            <Button
              variant="contained"
              size="large"
              onClick={() => {
                if (mode === 'popular') {
                  navigate('/');
                } else {
                  setMode('generate');
                  navigate('/composer');
                }
              }}
              sx={{
                minWidth: 200,
                fontSize: '1.1rem',
                py: 1.5,
                px: 4,
              }}
            >
              {mode === 'popular' ? 'Back to Home' : 'Create A Dish'}
            </Button>
          </Box>
        ) : (
          <List>
            {results.map((result) => (
              <Card
                key={result.id}
                sx={{ mb: 2, cursor: 'pointer' }}
                onClick={() => handleCardClick(result.id)}
              >
                <CardMedia
                  component="img"
                  height="140"
                  image={result.imageUrl}
                  alt={`Image of ${result.title}`}
                />
                <CardContent>
                  <Typography variant="h6" component="div">
                    {result.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    {result.description}
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap">
                    {result.categories?.slice(0, 3).map((category: string, index: number) => (
                      <Chip key={index} label={category} size="small" variant="outlined" />
                    ))}
                  </Stack>
                </CardContent>
              </Card>
            ))}
          </List>
        )}
      </Box>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      <Dialog open={regenerateDialog} onClose={() => setRegenerateDialog(false)}>
        <DialogTitle>Create Another Recipe</DialogTitle>
        <DialogContent>
          <Typography>How would you like to proceed?</Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-around', p: 2 }}>
          <Button onClick={() => setRegenerateDialog(false)} color="success" variant="contained">
            Cancel
          </Button>
          <Button onClick={handleChangeIngredients} color="success" variant="contained">
            Change Ingredients
          </Button>
          <Button onClick={handleSameIngredients} color="success" variant="contained">
            Same Ingredients
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Results;